/*
  SH Custom: Oct 2026 - two-tab menu drawer (Shop / Explore), abodeofsnow.jp.
  Opens from the desktop MENU button ([data-sh-ttm-open]) and from the theme's own phone menu icon
  (.main-header--tools .menu). The phone icon's click is taken in the capture phase, so Atlantic's
  mobile dropdown never opens while this drawer is enabled. No edits to atlantic.js.
*/
(() => {
  if (window.shTwoTabMenu) return; // the script tag can be re-inserted by the theme editor
  const OPENERS = '[data-sh-ttm-open], .main-header--tools .menu';
  let root = null;
  let tabs = [];
  let lastFocus = null;
  // SH Updated: Oct 2026 - opened by tap or click: focus the panel, not the Shop tab, so iOS does not draw the
  // keyboard outline on SHOP every time the menu opens (Erick's phone test, Oct 8). Keyboard opens keep the tab focus.
  let viaPointer = false;
  document.addEventListener('pointerdown', () => { viaPointer = true; }, true);
  document.addEventListener('keydown', () => { viaPointer = false; }, true);

  function setup() {
    // The newest drawer wins: the theme editor re-renders the header with a fresh one.
    const all = Array.from(document.querySelectorAll('[data-sh-ttm]'));
    // Setting unticked in the editor: the header re-renders without .sh-ttm-on, so drop the drawer.
    if (!document.querySelector('.sh-ttm-on')) {
      all.forEach((el) => el.remove());
      root = null;
      return;
    }
    const fresh = all.find((el) => !el.dataset.shTtmReady) || all[0];
    all.filter((el) => el !== fresh).forEach((el) => el.remove());
    root = fresh || null;
    if (!root) return;
    root.dataset.shTtmReady = 'true';
    // Fixed positioning must not be trapped by a transformed or sticky header ancestor.
    if (root.parentElement !== document.body) document.body.appendChild(root);
    tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    tabs.forEach((tab) => tab.addEventListener('click', () => selectTab(tab.dataset.shTtmTab, false)));
    root.addEventListener('keydown', onKeydown);
    document.querySelectorAll(OPENERS).forEach((el) => {
      el.setAttribute('aria-controls', 'sh-ttm');
      el.setAttribute('aria-expanded', 'false');
    });
  }

  const isOpen = () => !!root && root.classList.contains('is-open');

  function selectTab(name, moveFocus) {
    tabs.forEach((tab) => {
      const on = tab.dataset.shTtmTab === name;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const list = document.getElementById(tab.getAttribute('aria-controls'));
      if (list) list.hidden = !on;
      if (on && moveFocus) tab.focus();
    });
  }

  function setExpanded(value) {
    document.querySelectorAll(OPENERS).forEach((el) => el.setAttribute('aria-expanded', String(value)));
  }

  function open(trigger) {
    lastFocus = trigger || document.activeElement;
    root.inert = false;
    root.classList.add('is-open');
    document.documentElement.classList.add('sh-ttm-lock');
    setExpanded(true);
    const current = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
    const panel = root.querySelector('.sh-ttm__panel');
    requestAnimationFrame(() => {
      if (viaPointer && panel) {
        panel.tabIndex = -1;
        panel.focus({ preventScroll: true });
      } else if (current) current.focus();
    });
  }

  function close() {
    if (!isOpen()) return;
    root.classList.remove('is-open');
    root.inert = true;
    document.documentElement.classList.remove('sh-ttm-lock');
    setExpanded(false);
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!root) return;
      const opener = event.target.closest(OPENERS);
      if (opener) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (isOpen()) close();
        else open(opener);
        return;
      }
      if (event.target.closest('[data-sh-ttm-close]')) {
        event.preventDefault();
        close();
      }
    },
    true
  );

  function onKeydown(event) {
    const tabIndex = tabs.indexOf(document.activeElement);
    if (tabIndex !== -1 && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      event.preventDefault();
      const next = tabs[(tabIndex + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      selectTab(next.dataset.shTtmTab, true);
      return;
    }
    if (event.key === 'Tab') {
      const focusable = Array.from(
        root.querySelectorAll('.sh-ttm__panel button, .sh-ttm__panel a[href]')
      ).filter((el) => el.tabIndex !== -1 && el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  // Esc closes from anywhere while open, not only when focus is inside the drawer (QA, Oct 7 2026:
  // a click on empty panel space or a background window left focus outside, and Esc did nothing).
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen()) close();
  });

  // Theme editor: close before the header is swapped, set up again on the new markup.
  document.addEventListener('shopify:section:unload', close);
  document.addEventListener('shopify:section:load', setup);

  window.shTwoTabMenu = { open, close };
  setup();
})();
